import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Pencil, ChevronDown, MoreHorizontal } from "lucide-react";
import api from "../lib/api";
import { updateItem, deleteItem } from "../hooks/useEntity";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ACQ_RESOURCE, STAGE_KEYS, PIPELINE_STAGES, OUTREACH_METHODS, NEXT_ACTIONS,
  SELLER_MOTIVATION, SELLER_RESPONSE, fmtDate, money, logEntry, uid,
} from "./AcquisitionPipeline";

const RESOURCE = ACQ_RESOURCE;
const stageAccent = (s) => (PIPELINE_STAGES.find((x) => x.key === s)?.accent) || "border-border";

const Section = ({ title, children, grid = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4", accent }) => (
  <div className={`rounded-lg ${accent ? `border-t-2 ${accent} border-x border-b` : "border"} border-border bg-card p-5`}>
    <div className="text-sm font-semibold mb-4">{title}</div>
    <div className={grid}>{children}</div>
  </div>
);

// Inline field — click the value to edit; text/number save on blur or Enter,
// selects/dates save on change. Each field persists independently.
function InlineField({ label, value, type = "text", options, display, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(value ?? ""); }, [value]);

  const commit = async (val) => {
    if (String(val ?? "") === String(value ?? "")) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(val); } finally { setBusy(false); setEditing(false); }
  };

  const shown = display ? display(value) : (value === "" || value == null ? "—" : String(value));

  if (type === "select") {
    return (
      <div className="group" data-testid={testid}>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{label}</div>
        <Select value={value || ""} onValueChange={commit} disabled={busy}>
          <SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  }
  if (type === "date") {
    return (
      <div className="group" data-testid={testid}>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{label}</div>
        <Input type="date" value={value || ""} disabled={busy} onChange={(e) => commit(e.target.value)} className="h-9" />
      </div>
    );
  }
  return (
    <div className="group" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{label}</div>
      {editing ? (
        <div className="flex items-center gap-1">
          <Input
            autoFocus type={type} value={draft} disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => commit(draft)}
            onKeyDown={(e) => { if (e.key === "Enter") commit(draft); if (e.key === "Escape") { setDraft(value ?? ""); setEditing(false); } }}
            className="h-9"
          />
        </div>
      ) : (
        <button onClick={() => setEditing(true)} className="w-full text-left flex items-center justify-between gap-2 h-9 px-3 rounded-md border border-transparent hover:border-border hover:bg-secondary/30 transition-colors" data-testid={testid ? `${testid}-value` : undefined}>
          <span className={`text-sm ${shown === "—" ? "text-muted-foreground" : "text-foreground"} truncate`}>{shown}</span>
          <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
        </button>
      )}
    </div>
  );
}

export default function AcquisitionDeal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deal, setDeal] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);

  const load = () => api.get(`/${RESOURCE}/${id}`).then((r) => setDeal(r.data)).catch(() => toast.error("Deal not found")).finally(() => setLoading(false));
  useEffect(() => { load(); api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [id]);

  const saveField = async (key, value, activityText) => {
    const payload = { [key]: value };
    if (activityText) {
      const activity = Array.isArray(deal.activity) ? [...deal.activity] : [];
      activity.push(logEntry(activityText, key === "status" ? "status" : "offer", user?.name));
      payload.activity = activity;
    }
    const updated = await updateItem(RESOURCE, id, payload);
    setDeal(updated);
    toast.success("Saved");
  };

  const saveAssigned = async (uidVal) => {
    const u = users.find((x) => x.id === uidVal);
    const updated = await updateItem(RESOURCE, id, { assigned_to: uidVal, assigned_to_name: u ? (u.name || u.email) : "" });
    setDeal(updated);
    toast.success("Saved");
  };

  const derivedFirst = deal?.owner_first ?? (deal?.owner ? String(deal.owner).split(" ")[0] : "");
  const derivedLast = deal?.owner_last ?? (deal?.owner ? String(deal.owner).split(" ").slice(1).join(" ") : "");
  const saveName = async (key, val) => {
    const first = key === "owner_first" ? val : derivedFirst;
    const last = key === "owner_last" ? val : derivedLast;
    const owner = `${first || ""} ${last || ""}`.trim();
    const updated = await updateItem(RESOURCE, id, { [key]: val, owner_first: first, owner_last: last, owner });
    setDeal(updated);
    toast.success("Saved");
  };

  const saveZip = async (zip) => {
    const payload = { zip };
    try {
      if (/^\d{5}$/.test(String(zip))) {
        const r = await fetch(`https://api.zippopotam.us/us/${zip}`);
        if (r.ok) {
          const j = await r.json();
          const p = j.places && j.places[0];
          if (p) { payload.city = p["place name"]; payload.state = p["state abbreviation"]; }
        }
      }
    } catch { /* ignore lookup failure */ }
    const updated = await updateItem(RESOURCE, id, payload);
    setDeal(updated);
    toast.success(payload.city ? `Saved · ${payload.city}, ${payload.state}` : "Saved");
  };

  const addNote = async () => {
    if (!note.trim()) return;
    const activity = Array.isArray(deal.activity) ? [...deal.activity] : [];
    activity.push(logEntry(note.trim(), "note", user?.name));
    const updated = await updateItem(RESOURCE, id, { activity });
    setDeal(updated); setNote(""); toast.success("Activity logged");
  };

  const doDelete = async () => {
    try { await deleteItem(RESOURCE, id); toast.success("Deal deleted"); navigate("/acquisition"); }
    catch { toast.error("Could not delete"); }
  };

  if (loading) return <div className="p-6 lg:p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!deal) return <div className="p-6 lg:p-8 text-sm text-muted-foreground">Deal not found. <button className="text-primary" onClick={() => navigate("/acquisition")}>Back to pipeline</button></div>;

  const timeline = [...(deal.activity || [])].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <button onClick={() => navigate("/acquisition")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="deal-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to pipeline
      </button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight truncate" data-testid="deal-page-title">{deal.property_label || "Untitled"}</h1>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs" data-testid="deal-page-status">{deal.status}</Badge>
            <span className="text-sm text-muted-foreground truncate">{derivedFirst || derivedLast ? `${derivedFirst} ${derivedLast}`.trim() : "No owner"}</span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid="deal-actions-btn"><MoreHorizontal className="h-4 w-4 mr-1.5" />Actions<ChevronDown className="h-4 w-4 ml-1.5" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setConfirmDel(true)} data-testid="deal-delete-action">
              <Trash2 className="h-4 w-4 mr-2" />Delete deal
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="space-y-6">
        <Section title="Property & Owner" accent={stageAccent(deal.status)}>
          <InlineField label="Property / APN" value={deal.property_label} onSave={(v) => saveField("property_label", v)} testid="f-property" />
          <InlineField label="First Name" value={derivedFirst} onSave={(v) => saveName("owner_first", v)} testid="f-first" />
          <InlineField label="Last Name" value={derivedLast} onSave={(v) => saveName("owner_last", v)} testid="f-last" />
          <InlineField label="Street Address" value={deal.address} onSave={(v) => saveField("address", v)} testid="f-address" />
          <InlineField label="ZIP Code" value={deal.zip} onSave={saveZip} testid="f-zip" />
          <InlineField label="City" value={deal.city} onSave={(v) => saveField("city", v)} testid="f-city" />
          <InlineField label="State" value={deal.state} onSave={(v) => saveField("state", v)} testid="f-state" />
        </Section>

        <Section title="Outreach">
          <InlineField label="Outreach Status" value={deal.status} type="select" options={STAGE_KEYS} onSave={(v) => saveField("status", v, `Status: ${deal.status} → ${v}`)} testid="f-status" />
          <InlineField label="Outreach Method" value={deal.outreach_method} type="select" options={OUTREACH_METHODS} onSave={(v) => saveField("outreach_method", v)} testid="f-method" />
          <div data-testid="f-assigned">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Assigned To</div>
            <Select value={deal.assigned_to || ""} onValueChange={saveAssigned}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <InlineField label="Next Action" value={deal.next_action} type="select" options={NEXT_ACTIONS} onSave={(v) => saveField("next_action", v)} testid="f-next-action" />
          <InlineField label="Next Action Date" value={deal.next_action_date} type="date" onSave={(v) => saveField("next_action_date", v)} testid="f-next-date" />
          <InlineField label="Last Contact" value={deal.last_contact} type="date" onSave={(v) => saveField("last_contact", v)} testid="f-last-contact" />
          <InlineField label="Attempts" value={deal.attempts} type="number" onSave={(v) => saveField("attempts", Number(v) || 0)} testid="f-attempts" />
          <InlineField label="Seller Response" value={deal.seller_response} type="select" options={SELLER_RESPONSE} onSave={(v) => saveField("seller_response", v)} testid="f-response" />
          <InlineField label="Seller Motivation" value={deal.seller_motivation} type="select" options={SELLER_MOTIVATION} onSave={(v) => saveField("seller_motivation", v)} testid="f-motivation" />
        </Section>

        <Section title="Offer" grid="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InlineField label="Target Offer" value={deal.target_offer} type="number" display={(v) => money(v)} onSave={(v) => saveField("target_offer", Number(v) || 0, v ? `Target offer set to ${money(v)}` : null)} testid="f-target" />
          <InlineField label="Maximum Offer" value={deal.max_offer} type="number" display={(v) => money(v)} onSave={(v) => saveField("max_offer", Number(v) || 0, v ? `Max offer set to ${money(v)}` : null)} testid="f-max" />
        </Section>

        <Section title="Notes" grid="grid grid-cols-1 gap-4">
          <InlineField label="Notes" value={deal.notes} onSave={(v) => saveField("notes", v)} testid="f-notes" />
        </Section>

        <div className="rounded-lg border border-border bg-card p-5">
          <div className="text-sm font-semibold mb-3">Activity Timeline</div>
          <div className="flex items-start gap-2 mb-4">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Log activity — e.g. Called owner, left voicemail; Seller countered $155K" data-testid="deal-log-note" />
            <Button onClick={addNote} disabled={!note.trim()} data-testid="deal-log-btn">Log</Button>
          </div>
          <div className="space-y-2" data-testid="deal-timeline">
            {timeline.length === 0 && <div className="text-sm text-muted-foreground">No activity yet.</div>}
            {timeline.map((a) => (
              <div key={a.id || uid()} className="flex gap-3 text-sm border-l-2 border-border pl-3 py-0.5">
                <div className="w-24 shrink-0 text-muted-foreground">{fmtDate(a.date)}</div>
                <div className="flex-1 text-foreground/90">{a.text}{a.actor ? <span className="text-muted-foreground"> · {a.actor}</span> : null}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this deal?</AlertDialogTitle>
            <AlertDialogDescription>{deal.property_label} will be permanently removed from the pipeline.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-deal">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
